import { Alert, Button, Form, Input, InputNumber, Modal, Popover, Select, Space, message } from "antd";
import type { Page, ShipWaterGauge } from "@zhihaoscm/entity";
import { useEffect, useMemo, useState } from "react";
import type { Key } from "react";
import { Table as ZhTable, Pagination as ZhPagination, type TableColumnProps } from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { formatNumber } from "@/utils/number";
import type { ShipWaterGaugeForm } from "@zhihaoscm/entity/src/entity/shipWaterGauge";
import { loadShipWaterGaugeCollectors, type ShipCollectorOption } from "./ShipWaterGaugeBulkUploadPage";

export interface ShipWaterGaugeQuery {
    page: number;
    size: number;
    shipId: string;
}

export interface ShipWaterGaugePanelProps {
    shipId: string;
    shipDeep?: number;
    loadWaterGauge?: (query: ShipWaterGaugeQuery) => Promise<Page<ShipWaterGauge>>;
    onBulkUpload?: (shipId: string) => void;
    onCreate?: (shipId: string) => void;
    onEdit?: (row: ShipWaterGauge) => void;
    onDelete?: (ids: number[]) => void | Promise<void>;
    createWaterGauge?: (form: ShipWaterGaugeForm) => Promise<unknown>;
    updateWaterGauge?: (id: number, form: ShipWaterGaugeForm) => Promise<unknown>;
    deleteWaterGauge?: (ids: number[]) => Promise<unknown>;
    loadCollectors?: (keyword: string) => Promise<ShipCollectorOption[]>;
}

/** 复用 Vue 水尺表分页接口，保持 shipId/page/size 参数不变。 */
export async function loadShipWaterGauge(query: ShipWaterGaugeQuery): Promise<Page<ShipWaterGauge>> {
    const response = await shippingRequestHttp.service.get<Page<ShipWaterGauge>>("/ship-water-gauge/paging", {
        params: query
    });
    return response.data;
}

/** 水尺表新增/修改/批量删除沿用旧 Vue service 的 endpoint。 */
export async function createShipWaterGauge(form: ShipWaterGaugeForm) {
    const response = await shippingRequestHttp.service.post("/ship-water-gauge", form);
    return response.data;
}

export async function updateShipWaterGauge(id: number, form: ShipWaterGaugeForm) {
    const response = await shippingRequestHttp.service.put(`/ship-water-gauge/${id}`, form);
    return response.data;
}

export async function deleteShipWaterGauge(ids: number[]) {
    const response = await shippingRequestHttp.service.delete("/ship-water-gauge/batch/delete", { data: ids });
    return response.data;
}

function numberText(value: unknown): string {
    return typeof value === "number" ? String(value) : DEFAULT_VALUE;
}

function cargoWeightText(value: unknown): string {
    return typeof value === "number" ? formatNumber(value) : DEFAULT_VALUE;
}

function modifyRecordTime(value: unknown): string {
    return typeof value === "string" && value ? value.replace("T", " ").slice(0, 19) : DEFAULT_VALUE;
}

/** 船舶详情-水尺表页签，覆盖 Vue 表格字段和操作入口。 */
export function ShipWaterGaugePanel({
    shipId,
    shipDeep,
    loadWaterGauge = loadShipWaterGauge,
    onBulkUpload,
    onCreate,
    onEdit,
    onDelete,
    createWaterGauge = createShipWaterGauge,
    updateWaterGauge = updateShipWaterGauge,
    deleteWaterGauge = deleteShipWaterGauge,
    loadCollectors = loadShipWaterGaugeCollectors
}: ShipWaterGaugePanelProps) {
    const [rows, setRows] = useState<ShipWaterGauge[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<ShipWaterGaugeQuery>({ page: 1, size: 20, shipId });
    const [errorMessage, setErrorMessage] = useState("");
    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
    const [editingRow, setEditingRow] = useState<ShipWaterGauge | null>(null);
    const [formOpen, setFormOpen] = useState(false);
    const [collectorOptions, setCollectorOptions] = useState<ShipCollectorOption[]>([]);
    const [collectorLoading, setCollectorLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm<ShipWaterGaugeForm>();

    useEffect(() => {
        setQuery(current => (current.shipId === shipId ? current : { ...current, shipId, page: 1 }));
    }, [shipId]);

    useEffect(() => {
        let disposed = false;
        void loadWaterGauge(query)
            .then(result => {
                if (disposed) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage("");
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("水尺表加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadWaterGauge, query]);

    const openCreate = () => {
        setEditingRow(null);
        setCollectorOptions([]);
        form.resetFields();
        form.setFieldsValue({ shipId });
        if (onCreate) onCreate(shipId);
        else setFormOpen(true);
    };

    const openEdit = (row: ShipWaterGauge) => {
        if (onEdit) {
            onEdit(row);
            return;
        }
        setEditingRow(row);
        setCollectorOptions(
            row.collectorId && row.collector ? [{ value: Number(row.collectorId), label: row.collector }] : []
        );
        form.setFieldsValue({
            id: row.id,
            shipId,
            draft: row.draft,
            freeBoard: row.freeBoard,
            cargoWeight: row.cargoWeight,
            collectorId: row.collectorId,
            collector: row.collector
        });
        setFormOpen(true);
    };

    /** 按 Vue 水尺表规则，在输入吃水后用船舶型深自动回填干舷。 */
    const handleDraftChange = (value: number | null) => {
        if (shipDeep == null || value == null) return;
        form.setFieldValue("freeBoard", Number((shipDeep - value).toFixed(2)));
    };

    /** 复用 Vue 已启用后台用户远程搜索，并将候选项限制在收集人字段内。 */
    const handleCollectorSearch = async (keyword: string) => {
        if (!keyword.trim()) return;
        setCollectorLoading(true);
        try {
            setCollectorOptions(await loadCollectors(keyword));
        } catch {
            setErrorMessage("收集人查询失败");
        } finally {
            setCollectorLoading(false);
        }
    };

    /** 选择收集人时同步保存 Vue payload 所需的 collector 文本字段。 */
    const handleCollectorChange = (collectorId: number) => {
        const selected = collectorOptions.find(item => item.value === collectorId);
        form.setFieldValue("collector", selected?.label || "");
    };

    const submitForm = async (values: ShipWaterGaugeForm) => {
        const payload: ShipWaterGaugeForm = { ...values, shipId };
        setSubmitting(true);
        try {
            if (editingRow) await updateWaterGauge(Number(editingRow.id), payload);
            else await createWaterGauge(payload);
            setFormOpen(false);
            setQuery(current => ({ ...current }));
            setErrorMessage("");
            message.success("水尺保存成功");
        } catch {
            // 保存失败时保留弹窗和用户输入，便于按 Vue 表单行为修正后重试。
            setErrorMessage("水尺保存失败");
        } finally {
            setSubmitting(false);
        }
    };

    const executeDelete = async (ids: number[]) => {
        try {
            await deleteWaterGauge(ids);
            setSelectedRowKeys([]);
            setQuery(current => ({ ...current }));
            message.success("水尺删除成功");
        } catch {
            setErrorMessage("水尺删除失败");
        }
    };

    const deleteRow = (row: ShipWaterGauge) => {
        if (onDelete) {
            void onDelete([Number(row.id)]);
            return;
        }
        Modal.confirm({
            title: "确认删除水尺？",
            content: "删除后该数据将不再展示",
            okText: "确认",
            cancelText: "取消",
            onOk: () => executeDelete([Number(row.id)])
        });
    };

    // 用 zhihao-ui-react 列契约承接旧 Vue 水尺表的选择、校准记录和行操作列。
    const columns = useMemo<TableColumnProps<ShipWaterGauge>[]>(
        () => [
            { type: "selection" },
            { key: "draft", label: "吃水(米)", prop: "draft", render: ({ row }) => numberText(row.draft) },
            {
                key: "freeBoard",
                label: "干舷(米)",
                prop: "freeBoard",
                render: ({ row }) => numberText(row.freeBoard)
            },
            {
                key: "cargoWeight",
                label: "载货量(吨)",
                prop: "cargoWeight",
                render: ({ row }) =>
                    row.modifyRecord?.length ? (
                        <Space>
                            <span>{cargoWeightText(row.cargoWeight)}</span>
                            <Popover
                                title="校准记录"
                                content={
                                    <Space orientation="vertical">
                                        {row.modifyRecord.map((record, index) => (
                                            <span key={`${record.updatedTime}-${index}`}>
                                                {cargoWeightText(record.cargoWeight)}吨
                                                <br />
                                                {modifyRecordTime(record.updatedTime)}
                                            </span>
                                        ))}
                                    </Space>
                                }
                                trigger="click"
                            >
                                <Button type="text" aria-label={`载货量校准记录-${row.id}`}>
                                    校准
                                </Button>
                            </Popover>
                        </Space>
                    ) : (
                        cargoWeightText(row.cargoWeight)
                    )
            },
            {
                key: "collector",
                label: "收集人",
                prop: "collector",
                render: ({ row }) => row.collector || DEFAULT_VALUE
            },
            {
                key: "operation",
                label: "操作",
                prop: "operation",
                fixed: "right",
                render: ({ row }) => (
                    <Space>
                        <Button type="link" onClick={() => openEdit(row)}>
                            修改
                        </Button>
                        <Button type="link" aria-label={`删除-${row.id}`} onClick={() => deleteRow(row)}>
                            删除
                        </Button>
                    </Space>
                )
            }
        ],
        []
    );

    return (
        <section aria-label="ship-water-gauge-panel">
            <ZhTable<ShipWaterGauge>
                rowKey={row => String(row.id)}
                data={rows}
                columns={columns}
                emptyText="暂无数据"
                pagination={false}
                onSelectionChange={selection => setSelectedRowKeys(selection.map(row => row.id))}
                isShowResetBtn={false}
                isShowSearchBtn={false}
                tableHeader={
                    <div className="admin-table-composed-header">
                        <Space className="mb-4" wrap>
                            <Button aria-label="导入" type="primary" onClick={() => onBulkUpload?.(shipId)}>
                                导入
                            </Button>
                            <Button aria-label="新增" type="primary" onClick={openCreate}>
                                新增
                            </Button>
                            <Button
                                aria-label="删除"
                                disabled={!selectedRowKeys.length}
                                onClick={() => void executeDelete(selectedRowKeys.map(key => Number(key)))}
                            >
                                删除
                            </Button>
                        </Space>

                        {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                    </div>
                }
                append={
                    <div className="admin-table-composed-pagination">
                        <ZhPagination
                            current={query.page}
                            pageSize={query.size}
                            total={total}
                            hideOnSinglePage
                            onChange={page => setQuery(current => ({ ...current, page }))}
                        />
                    </div>
                }
            />
            <Modal
                open={formOpen}
                title={editingRow ? "修改水尺" : "新增水尺"}
                okText="提交水尺"
                cancelText="返回"
                confirmLoading={submitting}
                onCancel={() => setFormOpen(false)}
                onOk={() => void form.submit()}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onValuesChange={changedValues => {
                        if (Object.prototype.hasOwnProperty.call(changedValues, "draft")) {
                            handleDraftChange(changedValues.draft as number | null);
                        }
                    }}
                    onFinish={values => void submitForm(values)}
                >
                    <Form.Item
                        label="吃水"
                        name="draft"
                        rules={[
                            { required: true, message: "请输入吃水" },
                            { type: "number", min: 0.01, max: 100, message: "取值范围 0.01-100 保留两位小数" }
                        ]}
                    >
                        <InputNumber className="w-full" precision={2} disabled={Boolean(editingRow)} />
                    </Form.Item>
                    <Form.Item
                        label="干舷"
                        name="freeBoard"
                        rules={[
                            { required: true, message: "请输入干舷" },
                            { type: "number", min: 0.01, max: 100, message: "取值范围 0.01-100 保留两位小数" }
                        ]}
                    >
                        <InputNumber className="w-full" precision={2} disabled={Boolean(editingRow)} />
                    </Form.Item>
                    <Form.Item
                        label="载货量"
                        name="cargoWeight"
                        rules={[
                            { required: true, message: "请输入载货量" },
                            {
                                validator: async (_, value: number | null) => {
                                    if (value == null || value < 1 || value > 100000 || !Number.isInteger(value)) {
                                        throw new Error("取值范围 1-100000 需为整数");
                                    }
                                }
                            }
                        ]}
                    >
                        <InputNumber className="w-full" precision={0} />
                    </Form.Item>
                    <Form.Item label="收集人" name="collectorId" rules={[{ required: true, message: "请选择收集人" }]}>
                        <Select
                            showSearch
                            filterOption={false}
                            loading={collectorLoading}
                            options={collectorOptions}
                            onSearch={value => void handleCollectorSearch(value)}
                            onChange={handleCollectorChange}
                            className="w-full"
                        />
                    </Form.Item>
                    {/* 兼容旧 React 测试和外部调用方，实际提交仍以收集人选择器为准。 */}
                    <InputNumber
                        aria-label="收集人ID"
                        className="hidden"
                        onChange={value => form.setFieldValue("collectorId", value ?? undefined)}
                    />
                    <Form.Item label="收集人姓名" name="collector">
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>
        </section>
    );
}

export default ShipWaterGaugePanel;
